import { useNavigate } from 'react-router-dom';
import { BurgerMenu } from '@/components/kit';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFeedbackStore } from '@/stores/useFeedbackStore';
import {
  money, monthName, periodLabel, thisPeriod, useAccountsSummary, usePayables, usePayrollRun, useProfitAndLoss,
} from './lib/useAccounts';

/** Which door the screen opening the menu IS, so it can wear "Home". */
export type AccountantDoor =
  | 'home' | 'receivables' | 'bills' | 'expenses' | 'people'
  | 'payables' | 'tax' | 'pnl' | 'reports' | 'export';

/**
 * THE ACCOUNTANT'S ☰ — the ten doors of the books.
 *
 * ORDERED AS THE MONTH IS WORKED, not alphabetically: what is owed to the
 * clinic, what the clinic owes, who is paid, then what the whole thing adds up
 * to and what leaves the building. A list sorted by name would put Export
 * second and Tax ninth, which is nobody's working order.
 *
 * A PERSON'S STATEMENT IS NOT A DOOR. It opens from People, because it is
 * always about somebody — a menu entry would have to ask "whose?" first.
 */
export function AccountantMenu({
  door,
  onClose,
  counts,
}: {
  door: AccountantDoor;
  onClose: () => void;
  counts?: Partial<Record<AccountantDoor, string>>;
}) {
  const navigate = useNavigate();
  const { person, branch } = useShellIdentity();
  const logout = useAuthStore((s) => s.logout);
  const openFeedback = useFeedbackStore((s) => s.setOpen);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  const home = <span className="chip chip-brand chip-sm">Home</span>;

  /* THE LINE UNDER A DOOR IS A LIVE FIGURE (`g_a0`), read only while the menu
     is open — it is mounted on ☰, never hidden on every screen. */
  const period = thisPeriod();
  const { data: summary } = useAccountsSummary(period);
  const { data: payables } = usePayables();
  const { data: run } = usePayrollRun(period);
  const { data: pnl } = useProfitAndLoss(period);
  const overdue = (payables?.items ?? []).filter((i) => i.overdueDays > 0).length;
  const live = {
    home: summary ? `${periodLabel(period)} · ${money(summary.collectedMinor)} collected` : 'the month at a glance',
    receivables: summary
      ? `patients ${money(summary.patientsOweMinor)} · insurers ${money(summary.insurersOweMinor)}`
      : 'who owes, and how long',
    people: run ? `${monthName(period)} run · ${run.status}` : 'deals, runs and statements',
    payables: payables
      ? overdue ? `${money(payables.overdueMinor)} overdue` : `${money(payables.openMinor)} open`
      : 'what the clinic owes, and by when',
    pnl: pnl ? `${money(pnl.profitMinor)}${pnl.marginPercent === null ? '' : ` · ${pnl.marginPercent}%`}` : 'what the month came to',
  };
  const at = (k: AccountantDoor) => (door === k ? home : undefined);

  return (
    <BurgerMenu
      open
      onClose={onClose}
      roleChip="Accountant"
      doors={[
        { key: 'home', icon: 'home', label: 'Accounts',
          sub: counts?.home ?? live.home, badge: at('home'),
          onSelect: () => go('/accountant') },
        { key: 'bills', icon: 'file-text', label: 'Bills & claims', section: 'Money in',
          sub: counts?.bills ?? 'every invoice raised', badge: at('bills'),
          onSelect: () => go('/accountant/bills') },
        { key: 'receivables', icon: 'receipt', label: 'Receivables', section: 'Money in',
          sub: counts?.receivables ?? live.receivables, badge: at('receivables'),
          onSelect: () => go('/accountant/receivables') },
        { key: 'expenses', icon: 'shopping-cart', label: 'Expenses', section: 'Money out',
          sub: counts?.expenses ?? 'the spend side', badge: at('expenses'),
          onSelect: () => go('/accountant/expenses') },
        { key: 'people', icon: 'users', label: 'People we pay', section: 'Money out',
          sub: counts?.people ?? live.people, badge: at('people'),
          onSelect: () => go('/accountant/people') },
        { key: 'payables', icon: 'wallet', label: 'Payables', section: 'Money out',
          sub: counts?.payables ?? live.payables,
          badge: at('payables') ?? (overdue ? <span className="chip chip-danger chip-sm">{overdue}</span> : undefined),
          onSelect: () => go('/accountant/payables') },
        { key: 'tax', icon: 'scale', label: 'Tax report', section: 'The books',
          sub: counts?.tax ?? 'exempt against taxable', badge: at('tax'),
          onSelect: () => go('/accountant/tax') },
        { key: 'pnl', icon: 'trending-up', label: 'Profit & loss', section: 'The books',
          sub: counts?.pnl ?? live.pnl, badge: at('pnl'),
          onSelect: () => go('/accountant/pnl') },
        { key: 'reports', icon: 'chart-bar', label: 'Financial reports', section: 'The books',
          sub: counts?.reports ?? 'revenue · insurance · edits', badge: at('reports'),
          onSelect: () => go('/accountant/reports') },
        { key: 'export', icon: 'download', label: 'Export centre', section: 'The books',
          sub: counts?.export ?? 'the monthly close package', badge: at('export'),
          onSelect: () => go('/accountant/export') },
      ]}
      note={<>A bill, a run, a statement open from their lists — never from here. Read-only everywhere: this menu has no create.</>}
      account={{
        name: person?.name ?? 'This account',
        initials: person?.initials ?? 'AC',
        caption: 'account — devices · notifications',
        onSelect: () => go('/settings'),
      }}
      branch={branch}
      onAppearance={() => go('/settings#appearance')}
      onFeedback={() => { onClose(); openFeedback(true); }}
      onSignOut={() => void logout()}
    />
  );
}
